import React, { useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import gsap from "gsap";

/* ==================================================================
 * QR GATE — TEMPORARY
 *
 * Reused at every checkpoint. Pass title and body per location.
 *
 * Right now it shows a viewfinder and a skip button so the flow is
 * playable before the codes are printed and mounted in Porto-Vecchio.
 *
 * When the real thing goes in, this component keeps its shape:
 *   - "Open camera" starts a scanner (html5-qrcode or BarcodeDetector)
 *   - the decoded id goes to the server for validation
 *   - the server checks session, expected checkpoint, GPS proximity
 *   - only then does onScanned() fire
 *
 * The QR must never carry the clue itself. It identifies a location;
 * the server decides whether this player is allowed the content. Codes
 * get photographed and shared, so anything the code alone unlocks is
 * already public.
 * ================================================================== */

export default function QRGate({ title, body, copy, onScanned }) {
  const rootRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(rootRef.current.querySelectorAll("[data-stagger]"), {
        opacity: 0,
        y: 16,
        duration: 0.7,
        stagger: 0.13,
        ease: "power2.out",
      });
    }, rootRef);
    return () => ctx.revert();
  }, [title]);

  return (
    <Root ref={rootRef}>
      <Title data-stagger>{title}</Title>
      <Body data-stagger>{body}</Body>

      <Viewfinder data-stagger>
        <Corner $tl />
        <Corner $tr />
        <Corner $bl />
        <Corner $br />
        <ScanLine />
        <MarkGrid aria-hidden="true">
          {/* decorative only — not a real code */}
          {PATTERN.map((on, i) => (
            <MarkCell key={i} $on={on === 1} />
          ))}
        </MarkGrid>
      </Viewfinder>

      <DemoBlock data-stagger>
        <DemoNote>{copy.qrDemoNote}</DemoNote>
        <DemoButton onClick={onScanned}>{copy.qrDemoCta}</DemoButton>
      </DemoBlock>
    </Root>
  );
}

/* A fixed 7x7 pattern so the placeholder looks deliberate rather than
   like a broken image. Not scannable, and not meant to be. */
const PATTERN = [
  1, 1, 1, 0, 1, 1, 1,
  1, 0, 1, 0, 1, 0, 1,
  1, 1, 1, 0, 1, 1, 1,
  0, 0, 0, 1, 0, 0, 0,
  1, 1, 0, 1, 0, 1, 1,
  1, 0, 1, 1, 1, 0, 1,
  1, 1, 1, 0, 1, 1, 1,
];

/* ------------------------------------------------------------------ */

const Root = styled.div`
  position: fixed;
  inset: 0;
  z-index: 30;
  background: var(--ink);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  padding: calc(env(safe-area-inset-top) + 6vh) 8vw
    calc(env(safe-area-inset-bottom) + 5vh);
  text-align: center;
`;

const Title = styled.h2`
  margin: 0;
  font-family: var(--font-display);
  font-weight: 400;
  font-size: clamp(1.6rem, 7vw, 2.2rem);
  color: var(--parchment);
`;

const Body = styled.p`
  margin: 0;
  max-width: 32ch;
  font-family: var(--font-body);
  font-size: 0.94rem;
  line-height: 1.62;
  color: var(--parchment-2);
`;

const Viewfinder = styled.div`
  position: relative;
  width: min(58vw, 230px);
  aspect-ratio: 1;
  display: grid;
  place-items: center;
  margin: 0.6rem 0;
  overflow: hidden;
`;

const Corner = styled.span`
  position: absolute;
  width: 26px;
  height: 26px;
  border: 2px solid var(--brass);
  opacity: 0.75;

  ${(p) => p.$tl && "top: 0; left: 0; border-right: 0; border-bottom: 0;"}
  ${(p) => p.$tr && "top: 0; right: 0; border-left: 0; border-bottom: 0;"}
  ${(p) => p.$bl && "bottom: 0; left: 0; border-right: 0; border-top: 0;"}
  ${(p) => p.$br && "bottom: 0; right: 0; border-left: 0; border-top: 0;"}
`;

const sweep = keyframes`
  0%   { transform: translateY(-50%); opacity: 0; }
  15%  { opacity: 1; }
  85%  { opacity: 1; }
  100% { transform: translateY(50%); opacity: 0; }
`;

const ScanLine = styled.span`
  position: absolute;
  left: 8%;
  right: 8%;
  height: 1px;
  background: linear-gradient(to right, transparent, var(--brass), transparent);
  box-shadow: 0 0 14px rgba(192, 139, 62, 0.7);
  animation: ${sweep} 2.8s ease-in-out infinite;
`;

const MarkGrid = styled.div`
  width: 62%;
  aspect-ratio: 1;
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
  opacity: 0.34;
`;

const MarkCell = styled.span`
  background: ${(p) => (p.$on ? "var(--parchment)" : "transparent")};
`;

const DemoBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.9rem;
  margin-top: 0.6rem;
  padding-top: 1.6rem;
  border-top: 1px dashed rgba(232, 220, 196, 0.16);
  width: min(100%, 22rem);
`;

const DemoNote = styled.div`
  font-family: ui-monospace, Menlo, monospace;
  font-size: 0.62rem;
  line-height: 1.6;
  color: var(--parchment);
  opacity: 0.36;
  max-width: 30ch;
`;

const DemoButton = styled.button`
  padding: 0.85rem 2.2rem;
  border: 1px solid rgba(232, 220, 196, 0.28);
  font-size: 0.66rem;
  letter-spacing: 0.26em;
  text-transform: uppercase;
  color: var(--parchment);
  opacity: 0.8;
  transition: opacity 0.3s ease, background 0.3s ease;

  &:hover {
    opacity: 1;
    background: rgba(232, 220, 196, 0.06);
  }
`;
